import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

export interface UserLocation {
  home_zip_code: string | null;
  home_latitude: number | null;
  home_longitude: number | null;
  home_city: string | null;
  home_state: string | null;
  local_radius_miles: number;
}

export const useUserLocation = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: location, isLoading } = useQuery({
    queryKey: ['userLocation', user?.id],
    queryFn: async (): Promise<UserLocation | null> => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('users')
        .select('home_zip_code, home_latitude, home_longitude, home_city, home_state, local_radius_miles')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user location:', error);
        return null;
      }

      return data as UserLocation | null;
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  const updateLocationMutation = useMutation({
    mutationFn: async (locationData: Partial<UserLocation>) => {
      if (!user?.id) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('users')
        .upsert({
          id: user.id,
          ...locationData,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userLocation', user?.id] });
      toast({
        title: "Location updated",
        description: "Your home location has been saved.",
      });
    },
    onError: (error) => {
      console.error('Error updating location:', error);
      toast({
        title: "Error",
        description: "Failed to update location. Please try again.",
        variant: "destructive",
      });
    },
  });

  const hasLocation = !!(location?.home_latitude && location?.home_longitude);

  return {
    location,
    isLoading,
    hasLocation,
    updateLocation: updateLocationMutation.mutate,
    isUpdating: updateLocationMutation.isPending,
  };
};
